import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import { fr } from "date-fns/locale";
import games from "../../data/games";
import ChangePasswordModal from "./ChangePasswordModal";
import TeamBuilder from "./TeamBuilder";
import { useToast } from "../../components/ui/Toast";
import ProfileScheduleModal from "./ProfileScheduleModal";
import ProfileSessionMailModal from "./ProfileSessionMailModal";
import ProfileGdprModal from "./ProfileGdprModal";
import {
  Package,
  Search,
  RotateCcw,
  ChevronDown,
  Mail,
  Copy,
  CalendarDays,
  AlertTriangle,
  Info,
} from "lucide-react";

function Profile() {
  const accountRef = useRef(null);

  const [me, setMe] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const [showChangePwdModal, setShowChangePwdModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState([
    "scheduled",
    "in_progress",
    "active",
    "pending",
  ]);
  const [packTypeFilter, setPackTypeFilter] = useState("all");
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);
  const [showGdprModal, setShowGdprModal] = useState(false);
  const [gdprChecked, setGdprChecked] = useState(false);
  const [gdprText, setGdprText] = useState("");
  const [gdprLoading, setGdprLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
  const [showSessionMailModal, setShowSessionMailModal] = useState(false);
  const [mailSession, setMailSession] = useState(null);

  const API = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) { navigate("/login"); return; }
      try {
        const [profileRes, sessionsRes] = await Promise.all([
          axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/sessions/mine`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setMe(profileRes.data);
        setSessions(sessionsRes.data);
      } catch (err) {
        console.error(err);
        navigate("/login");
      }
    };
    fetchProfile();
  }, [navigate, API]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!showAccount) return;
      if (accountRef.current && !accountRef.current.contains(e.target)) setShowAccount(false);
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [showAccount]);

  const FRONT_BASE_URL = process.env.REACT_APP_FRONT_BASE_URL || process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";
  const playUrl = `${FRONT_BASE_URL}/play`;
  const guideUrl = `${FRONT_BASE_URL}/guide`;

  const handleDownloadInvoice = async (paymentId) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/api/payments/${paymentId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data?.url) window.open(data.url, "_blank", "noopener");
      else toast.error("Facture introuvable pour ce pack.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Erreur lors de la récupération de la facture.");
    }
  };

  const formatDateRange = (s) => {
    if (!s?.startAt) return "à définir";
    const start = new Date(s.startAt).toLocaleDateString();
    const end = s.endAt ? new Date(s.endAt).toLocaleDateString() : null;
    return end ? `${start} → ${end}` : start;
  };

  const buildSessionEmailTemplate = (s) => {
    if (!s?.startAt) {
      return {
        subject: "Session à planifier — code indisponible",
        body: `Bonjour,\n\nCette session n'est pas encore planifiée.\n➡️ Merci de définir une date dans le tableau de bord avant d'envoyer ce mail.\n\nGuide : ${guideUrl}\n`,
      };
    }
    const gameName = getGameName(s.gameId);
    const dateStr = formatDateRange(s);
    const subject = `Escape game digital — ${gameName} — Code de session ${s.code}`;
    const body = `Bonjour,

Voici les informations pour rejoindre la session d'escape game digital.

Jeu : ${gameName}
Date : ${dateStr}
Code session : ${s.code}

Marche à suivre :
1) Lancez une visio avec votre équipe (Teams / Meet / Zoom…)
2) Ouvrez la page de jeu : ${playUrl}
3) Entrez le code session : ${s.code}
4) Créez votre profil joueur et suivez les instructions

Guide (pré-requis + déroulé) :
${guideUrl}

Merci de vous connecter quelques minutes en avance.

Bonne partie,
`;
    return { subject, body };
  };

  const copyToClipboard = async (txt, successMsg = "Copié.") => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(successMsg, { title: "OK" });
    } catch (e) {
      console.error(e);
      toast.error("Impossible de copier automatiquement. Copiez manuellement.", { title: "Erreur" });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-slate-100 text-slate-700 border border-slate-200";
      case "scheduled": return "bg-amber-50 text-amber-800 border border-amber-100";
      case "active": return "bg-emerald-50 text-emerald-700 border border-emerald-100";
      case "in_progress": return "bg-blue-50 text-blue-700 border border-blue-100";
      case "used": return "bg-indigo-50 text-indigo-700 border border-indigo-100";
      case "expired": return "bg-rose-50 text-rose-700 border border-rose-100";
      default: return "bg-slate-100 text-slate-700 border border-slate-200";
    }
  };

  const getGameName = (id) => {
    const game = games.find((g) => g.id === id);
    return game ? game.name : `Jeu #${id}`;
  };

  const statusOptions = [
    { value: "scheduled", label: "Planifiée" },
    { value: "in_progress", label: "En cours" },
    { value: "active", label: "Active" },
    { value: "pending", label: "Non planifiée" },
    { value: "used", label: "Utilisée" },
    { value: "expired", label: "Expirée" },
  ];

  const getStatusLabel = (status) => {
    const opt = statusOptions.find((o) => o.value === status);
    return opt ? opt.label : status;
  };

  const getPackTypeLabel = (pt) => {
    const v = String(pt || "").toLowerCase();
    if (v === "extended") return "Semaine flexible";
    if (v === "normal") return "Journée";
    return pt || "—";
  };

  const isSessionPlanned = (s) => !!s?.startAt;

  const packTypes = Array.from(new Set(sessions.map((s) => s.packType).filter(Boolean)));

  const openSessionMail = (s) => {
    if (!s?.startAt) {
      toast.error("Planifiez d'abord la date : le mail est indisponible tant que la session n'est pas planifiée.");
      return;
    }
    setMailSession(s);
    setShowSessionMailModal(true);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <span className="ml-1 text-[10px] opacity-30">↕</span>;
    return <span className="ml-1 text-[10px]">{sortConfig.direction === "asc" ? "▲" : "▼"}</span>;
  };

  const canRequestReschedule = (session) =>
    session.status !== "in_progress" && session.status !== "used";

  const toggleStatus = (value) => {
    setStatusFilter((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter(["scheduled", "in_progress", "active", "pending"]);
    setPackTypeFilter("all");
    setStartDateFilter(null);
    setEndDateFilter(null);
    setSortConfig({ key: "id", direction: "desc" });
  };

  const requestGdprDeletion = async () => {
    try {
      setGdprLoading(true);
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/gdpr/request-deletion`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Votre demande a été enregistrée. Nous reviendrons vers vous.");
      setShowGdprModal(false);
      setGdprChecked(false);
      setGdprText("");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi de la demande.");
    } finally {
      setGdprLoading(false);
    }
  };

  // ── Filtrage ────────────────────────────────────────────────────────────────
  const filteredSessions = sessions.filter((s) => {
    if (statusFilter.length > 0 && !statusFilter.includes(s.status)) return false;
    if (packTypeFilter !== "all" && s.packType !== packTypeFilter) return false;
    if (startDateFilter && (!s.startAt || new Date(s.startAt) < startDateFilter)) return false;
    if (endDateFilter && (!s.startAt || new Date(s.startAt) > endDateFilter)) return false;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const code = (s.code || "").toLowerCase();
      const gameName = getGameName(s.gameId).toLowerCase();
      if (!code.includes(q) && !gameName.includes(q)) return false;
    }
    return true;
  });

  // ── Groupement & tri ────────────────────────────────────────────────────────
  const groupsMap = new Map();
  const groups = [];

  filteredSessions.forEach((s) => {
    const key = s.paymentId != null ? `pack-${String(s.paymentId)}` : `solo-${s.id}`;
    let group = groupsMap.get(key);
    if (!group) {
      let sortId = s.id || 0;
      if (s.paymentId != null && /^\d+$/.test(String(s.paymentId))) sortId = Number(s.paymentId);
      group = {
        key, paymentId: s.paymentId || null, sessions: [], sortId,
        minCode: s.code || "", statusForSort: s.status || "", dateForSort: s.startAt ? new Date(s.startAt).getTime() : 0,
      };
      groupsMap.set(key, group);
      groups.push(group);
    }
    group.sessions.push(s);
    const code = s.code || "";
    if (code && (!group.minCode || code < group.minCode)) group.minCode = code;
    const st = s.status || "";
    if (!group.statusForSort || st < group.statusForSort) group.statusForSort = st;
    if (s.startAt) {
      const t = new Date(s.startAt).getTime();
      if (!group.dateForSort || t < group.dateForSort) group.dateForSort = t;
    }
  });

  const sortedGroups = groups.sort((a, b) => {
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    if (sortConfig.key === "code") return (a.minCode || "").toLowerCase().localeCompare((b.minCode || "").toLowerCase()) * dir;
    if (sortConfig.key === "status") return (a.statusForSort || "").toLowerCase().localeCompare((b.statusForSort || "").toLowerCase()) * dir;
    if (sortConfig.key === "date") { const d = (a.dateForSort || 0) - (b.dateForSort || 0); return d === 0 ? 0 : d < 0 ? -1 * dir : 1 * dir; }
    const d = (a.sortId || 0) - (b.sortId || 0);
    return d === 0 ? 0 : d < 0 ? -1 * dir : 1 * dir;
  });

  const hasAnySessions = sessions.length > 0;
  const hasResults = sortedGroups.length > 0;
  const hasUnplanned = sessions.some((s) => !s.startAt && s.status === "pending");

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-10 text-slate-900">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* ── Header ── */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              Mon espace
            </h2>
            {me && (
              <p className="text-sm text-slate-500 mt-1">
                {me.name} · {me.email}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <a
              href="#team-builder"
              className="px-4 py-2 rounded-full text-sm font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition shadow-sm"
            >
              Outils avancés
            </a>
            <button
              onClick={() => navigate("/catalogue")}
              className="px-4 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition shadow-sm"
            >
              + Nouvelle session
            </button>

            {/* Mon compte dropdown */}
            <div className="relative" ref={accountRef}>
              <button
                onClick={() => setShowAccount((v) => !v)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50 transition shadow-sm"
                aria-haspopup="true"
                aria-expanded={showAccount}
              >
                Mon compte
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAccount ? "rotate-180" : ""}`} />
              </button>
              {showAccount && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
                  <button
                    onClick={() => { setShowChangePwdModal(true); setShowAccount(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition"
                  >
                    Changer mon mot de passe
                  </button>
                  <button
                    onClick={() => { setShowGdprModal(true); setShowAccount(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition"
                  >
                    RGPD &amp; données
                  </button>
                  <div className="h-px bg-slate-100" />
                  <button
                    onClick={() => { localStorage.removeItem("token"); navigate("/login"); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition"
                  >
                    Se déconnecter
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Bannière contextuelle ── */}
        {hasUnplanned ? (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <div>
              <span className="font-semibold">Action requise :</span> vous avez des sessions <span className="font-semibold">non planifiées</span>.{" "}
              Tant que la date n'est pas fixée, le code session et le mail restent masqués.
            </div>
          </div>
        ) : hasAnySessions ? (
          <div className="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            <Info className="h-4 w-4 shrink-0 mt-0.5 text-indigo-500" />
            <div>
              <span className="font-semibold">À faire :</span> partagez le <span className="font-semibold">code session</span> avec les joueurs.
              Cliquez sur "Préparer le mail" pour générer un message prêt à copier-coller.
            </div>
          </div>
        ) : null}

        {/* ── Tableau sessions ── */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">

          {/* Filtres */}
          <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-wrap gap-3 items-end">
            {/* Recherche */}
            <div className="flex flex-col w-full sm:w-60">
              <label className="text-xs font-medium text-slate-500 mb-1">Recherche</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Code ou nom du jeu"
                  className="pl-9 pr-3 py-2 w-full rounded-lg bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 text-sm text-slate-900 placeholder-slate-400 shadow-sm"
                />
              </div>
            </div>

            {/* Statuts */}
            <div className="flex flex-col flex-1 min-w-[220px]">
              <label className="text-xs font-medium text-slate-500 mb-1">Statuts</label>
              <div className="flex flex-wrap gap-1.5">
                {statusOptions.map((opt) => {
                  const active = statusFilter.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleStatus(opt.value)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition shadow-sm ${
                        active
                          ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Type de pack */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-slate-500 mb-1">Type</label>
              <select
                value={packTypeFilter}
                onChange={(e) => setPackTypeFilter(e.target.value)}
                className="px-3 py-2 rounded-lg bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 text-sm text-slate-900 min-w-[140px] shadow-sm"
              >
                <option value="all">Tous</option>
                {packTypes.map((pt) => (
                  <option key={pt} value={pt}>{getPackTypeLabel(pt)}</option>
                ))}
              </select>
            </div>

            {/* Dates */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-slate-500 mb-1">Du</label>
              <DatePicker
                selected={startDateFilter}
                onChange={(d) => setStartDateFilter(d)}
                locale={fr}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de début"
                className="px-3 py-2 rounded-lg bg-white border border-slate-200 focus:outline-none text-sm text-slate-900 placeholder-slate-400 shadow-sm w-36"
                calendarClassName="light-calendar"
                dayClassName={() => "text-white"}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-medium text-slate-500 mb-1">Au</label>
              <DatePicker
                selected={endDateFilter}
                onChange={(d) => setEndDateFilter(d)}
                locale={fr}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de fin"
                className="px-3 py-2 rounded-lg bg-white border border-slate-200 focus:outline-none text-sm text-slate-900 placeholder-slate-400 shadow-sm w-36"
                calendarClassName="light-calendar"
                dayClassName={() => "text-white"}
              />
            </div>

            <button
              type="button"
              onClick={resetFilters}
              className="self-end inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white hover:bg-slate-100 text-xs font-medium text-slate-600 border border-slate-200 shadow-sm transition"
            >
              <RotateCcw className="h-3 w-3" />
              Réinitialiser
            </button>
          </div>

          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wide border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left">
                    <button type="button" onClick={() => handleSort("code")} className="flex items-center gap-1 hover:text-slate-700 transition uppercase">
                      Session {renderSortIcon("code")}
                    </button>
                  </th>
                  <th className="py-3 px-4 text-left">Jeu</th>
                  <th className="py-3 px-4 text-left">Pack</th>
                  <th className="py-3 px-4 text-left">
                    <button type="button" onClick={() => handleSort("status")} className="flex items-center gap-1 hover:text-slate-700 transition uppercase">
                      Statut {renderSortIcon("status")}
                    </button>
                  </th>
                  <th className="py-3 px-4 text-left">
                    <button type="button" onClick={() => handleSort("date")} className="flex items-center gap-1 hover:text-slate-700 transition uppercase">
                      Dates {renderSortIcon("date")}
                    </button>
                  </th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedGroups.map((group) => {
                  const isSolo = group.key.startsWith("solo-");
                  const list = group.sessions;

                  return (
                    <React.Fragment key={group.key}>
                      {/* Ligne de pack */}
                      {!isSolo && (
                        <tr className="bg-indigo-50/60">
                          <td colSpan="6" className="py-2.5 px-4 border-t border-indigo-100">
                            <div className="flex items-center justify-between gap-3">
                              <span className="inline-flex items-center gap-2 text-sm text-indigo-900 font-medium">
                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 text-white text-[11px] px-2.5 py-0.5 shadow-sm">
                                  <Package className="h-3 w-3" />
                                  Pack
                                </span>
                                #{group.paymentId} — {list.length} session{list.length > 1 ? "s" : ""}
                              </span>
                              <button
                                onClick={() => handleDownloadInvoice(group.paymentId)}
                                className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition shadow-sm"
                              >
                                Télécharger la facture
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}

                      {/* Lignes de sessions */}
                      {list.map((s) => (
                        <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">

                          {/* Colonne session — code + actions principales */}
                          <td className="py-3 px-4">
                            {isSessionPlanned(s) ? (
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-indigo-700 font-semibold tracking-wide">
                                    {s.code}
                                  </span>
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700">
                                    À partager
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(s.code, "Code session copié.")}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition shadow-sm"
                                  >
                                    <Copy className="h-3 w-3" />
                                    Copier le code
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openSessionMail(s)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-500 transition shadow-sm"
                                  >
                                    <Mail className="h-3 w-3" />
                                    Préparer le mail
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-slate-400 font-semibold tracking-wide">••••••</span>
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900">
                                    À planifier
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-400">
                                  Fixez une date pour afficher le code.
                                </p>
                              </div>
                            )}
                          </td>

                          <td className="py-3 px-4 text-slate-700">{getGameName(s.gameId)}</td>

                          <td className="py-3 px-4 text-slate-600">{getPackTypeLabel(s.packType)}</td>

                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(s.status)}`}>
                              {getStatusLabel(s.status)}
                            </span>
                          </td>

                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {s.startAt
                              ? `${new Date(s.startAt).toLocaleDateString()} → ${new Date(s.endAt).toLocaleDateString()}`
                              : "—"}
                          </td>

                          {/* Colonne actions — planification / report */}
                          <td className="py-3 px-4">
                            {s.startAt ? (
                              s.hasPendingRequest ? (
                                <span className="inline-flex items-center gap-1.5 text-amber-800 text-xs bg-amber-50 border border-amber-100 px-2.5 py-1 rounded-full">
                                  <CalendarDays className="h-3 w-3" />
                                  Report en attente
                                </span>
                              ) : canRequestReschedule(s) ? (
                                <button
                                  onClick={() => setActiveSession({ ...s, rescheduleMode: true })}
                                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium underline underline-offset-2 transition"
                                >
                                  Demander un report
                                </button>
                              ) : (
                                <span className="text-slate-400 text-xs">Session terminée</span>
                              )
                            ) : (
                              <button
                                type="button"
                                onClick={() => setActiveSession(s)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-500 text-white hover:bg-amber-400 transition shadow-sm"
                              >
                                <CalendarDays className="h-3 w-3" />
                                Choisir une date
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}

                {!hasResults && hasAnySessions && (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-slate-400 italic text-sm">
                      Aucune session ne correspond aux filtres.
                    </td>
                  </tr>
                )}
                {!hasAnySessions && (
                  <tr>
                    <td colSpan="6" className="text-center py-10">
                      <p className="text-slate-400 italic text-sm mb-3">Aucune session disponible.</p>
                      <button
                        onClick={() => navigate("/catalogue")}
                        className="px-4 py-2 rounded-full text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition shadow-sm"
                      >
                        Acheter ma première session
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Modale planification */}
          {activeSession && (
            <ProfileScheduleModal
              activeSession={activeSession}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onClose={() => { setActiveSession(null); setSelectedDate(null); }}
              onSaved={(data) => setSessions(data)}
              toast={toast}
              API={API}
            />
          )}
        </div>

        {/* ── Outils avancés (TeamBuilder) ── */}
        <div id="team-builder">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <details className="group">
              <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Outils avancés — Répartition d'équipes
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Créez des équipes automatiquement et générez les e-mails à envoyer. Sessions étendues uniquement.
                  </p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 pt-2 border-t border-slate-200">
                <TeamBuilder sessions={sessions} guideUrl={guideUrl} />
              </div>
            </details>
          </div>
        </div>

      </div>

      {/* ── Modales ── */}
      {showSessionMailModal && mailSession && (
        <ProfileSessionMailModal
          mailSession={mailSession}
          onClose={() => { setShowSessionMailModal(false); setMailSession(null); }}
          buildSessionEmailTemplate={buildSessionEmailTemplate}
          copyToClipboard={copyToClipboard}
          getGameName={getGameName}
        />
      )}
      {showChangePwdModal && (
        <ChangePasswordModal API={API} onClose={() => setShowChangePwdModal(false)} />
      )}
      {showGdprModal && (
        <ProfileGdprModal
          gdprChecked={gdprChecked} setGdprChecked={setGdprChecked}
          gdprText={gdprText} setGdprText={setGdprText}
          gdprLoading={gdprLoading}
          onClose={() => { setShowGdprModal(false); setGdprChecked(false); setGdprText(""); }}
          onConfirm={requestGdprDeletion}
        />
      )}
    </div>
  );
}

export default Profile;